import { NavLink } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Boxes, Users, LogOut, CalendarDays, Inbox, PartyPopper, NotebookPen } from "lucide-react";
import { useLang } from "../context/LangContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { ProfileDialog } from "./ProfileDialog";

export const NAV_LINKS = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link" },
  { to: "/orders", key: "orders", icon: ClipboardList, testid: "nav-orders-link" },
  { to: "/my-orders", key: "my_orders", icon: Inbox, testid: "nav-my-orders-link" },
  { to: "/daily-reports", key: "daily_report", icon: NotebookPen, testid: "nav-daily-reports-link" },
  { to: "/events", key: "events", icon: PartyPopper, testid: "nav-events-link" },
  { to: "/calendar", key: "calendar", icon: CalendarDays, testid: "nav-calendar-link" },
  { to: "/equipment", key: "equipment", icon: Boxes, testid: "nav-equipment-link" },
  { to: "/users", key: "users", icon: Users, testid: "nav-users-link", admin: true },
];

export const visibleLinksFor = (user) => NAV_LINKS.filter((l) => !l.admin || user?.role === "admin");

export function BrandMark({ size = "w-11 h-11 rounded-xl" }) {
  return (
    <div className={`${size} overflow-hidden flex-shrink-0 flex items-center justify-center`} style={{ background: "#0A3F4A" }}>
      <img src="/cen21-logo.webp" alt="Cen21" className="w-full h-full object-contain" style={{ transform: "scale(2.4)" }} />
    </div>
  );
}

export function NavLinks({ links, onNavigate, withTestId = true }) {
  const { t } = useLang();
  return links.map((l) => (
    <NavLink
      key={l.to}
      to={l.to}
      end={l.to === "/"}
      onClick={onNavigate}
      data-testid={withTestId ? l.testid : undefined}
      className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
    >
      <l.icon className="w-4 h-4" />
      {t(l.key)}
    </NavLink>
  ));
}

export function Sidebar({ user, links, onLogout }) {
  const { t } = useLang();
  const initials = (user?.name || user?.email || "?").slice(0, 2).toUpperCase();
  return (
    <aside className="hidden lg:flex fixed top-0 left-0 h-full w-64 glass border-r border-white/5 flex-col p-5 z-20">
      <div className="flex items-center gap-3 mb-8">
        <BrandMark />
        <div className="min-w-0">
          <div className="font-bold tracking-tight truncate" style={{ fontFamily: "Outfit" }}>{t("app_name")}</div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Marketing OS</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1"><NavLinks links={links} /></nav>
      <div className="border-t border-white/5 pt-4 mt-4">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="h-9 w-9 bg-indigo-500/20 text-indigo-200">
            <AvatarFallback className="bg-indigo-500/20 text-indigo-200 text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm truncate">{user?.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{t(user?.role || "member")}</div>
          </div>
        </div>
        <ProfileDialog />
        <Button data-testid="sidebar-logout-btn" variant="ghost" size="sm" onClick={onLogout} className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5">
          <LogOut className="w-4 h-4 mr-2" />
          {t("logout")}
        </Button>
      </div>
    </aside>
  );
}

export function MobileMenu({ links, onNavigate, onLogout }) {
  const { t } = useLang();
  return (
    <div className="lg:hidden fixed inset-0 top-14 z-20 bg-slate-950/90 backdrop-blur-lg p-4">
      <nav className="space-y-1">
        <NavLinks links={links} onNavigate={onNavigate} withTestId={false} />
        <div className="mt-4"><ProfileDialog /></div>
        <button onClick={onLogout} className="sidebar-link w-full text-left mt-4">
          <LogOut className="w-4 h-4" />
          {t("logout")}
        </button>
      </nav>
    </div>
  );
}
